"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.NavigationContextProvider = void 0;
var _auth = require("@ori/auth");
var _checkoutUtils = require("@ori/checkout-utils");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _shoppingContext = require("@ori/shopping-context");
var _react = require("react");
var _navigationContext = require("../contexts/navigationContext");
var _hooks = require("../hooks");
var _logger = require("../logger");
var _models = require("../models");
var _utils = require("../utils");
var _jsxRuntime = require("react/jsx-runtime");
const NavigationContextProvider = ({
  children
}) => {
  const {
    isUserVerified,
    isAnonymousUser
  } = (0, _auth.useToken)();
  const {
    formatMessage
  } = (0, _hooks.useTranslations)();
  const [selectedStep, setSelectedStep] = (0, _react.useState)(_models.ShoppingStep.Basket);
  const [registrationId, setRegistrationId] = (0, _react.useState)('');
  const [isRegistrationVisible, setIsRegistrationVisible] = (0, _react.useState)(false);
  const [billingState, setBillingState] = (0, _react.useState)('disabled');
  const {
    activeProviderId,
    cardConsentIsFulfilled,
    mutatePaymentModel,
    paymentModel,
    isPaymentModelLoading,
    setActiveProviderId,
    setShouldMutatePaymentModel,
    shouldMutatePaymentModel
  } = (0, _hooks.useGetPaymentModel)(selectedStep);
  const {
    isLoaded,
    shoppingContext: {
      customerId = _auth.NOT_LOGGED_IN_USER_ID,
      isAnonymous,
      isDeliveryChannelSelected,
      shoppingBag: {
        addedItems = [],
        backorderItems = [],
        offers: shoppingBagOffers = [],
        registrationFee,
        renewalFee,
        shoppingBagItems = [],
        subscriptions = []
      } = {},
      unitDataCollection = []
    } = {}
  } = (0, _shoppingContext.useShoppingContext)();
  const [orderState, setOrderState] = (0, _react.useState)(_models.OrderState.None);
  const [continueButtonTooltip, setContinueButtonTooltip] = (0, _react.useState)();
  const [isMandatoryBillingAddressMissing, setIsMandatoryBillingAddressMissing] = (0, _react.useState)(false);
  const [isMandatoryEmailMissing, setIsMandatoryEmailMissing] = (0, _react.useState)(false);
  const [isMandatoryPhoneMissing, setIsMandatoryPhoneMissing] = (0, _react.useState)(false);
  const swrOptionsPrerequisites = (0, _react.useMemo)(() => ({
    enabled: isUserVerified && !isAnonymousUser && selectedStep === _models.ShoppingStep.Checkout,
    onSuccess: data => {
      (0, _logger.logDebug)('useGetCustomerCheckoutPrerequisites() request finished successfully with', data);
      setIsMandatoryBillingAddressMissing(Boolean(data.address?.isMissing && data.address.isMandatory));
      setIsMandatoryEmailMissing(Boolean(data.email?.isMissing && data.email.isMandatory));
      setIsMandatoryPhoneMissing(Boolean(data.telephone?.isMissing && data.telephone.isMandatory));
    },
    ..._checkoutUtils.errorHandlingSwrOptions
  }), [isAnonymousUser, isUserVerified, selectedStep]);
  const {
    data: customerCheckoutPrerequisites = {},
    isValidating: areCustomerCheckoutPrerequisitesLoading,
    mutate: mutateCustomerCheckoutPrerequisites
  } = (0, _globalApiClientReact.useGetCustomerCheckoutPrerequisites)(customerId, undefined, {
    swr: swrOptionsPrerequisites
  });

  // eslint-disable-next-line complexity -- TODO: Refactor
  (0, _react.useEffect)(() => {
    if (isLoaded && !isAnonymous) {
      const hasNoBasketItems = !shoppingBagItems.length && !addedItems.length && !backorderItems.length && !subscriptions.length && !shoppingBagOffers.length && !registrationFee && !renewalFee;
      if (hasNoBasketItems && !unitDataCollection.filter(x => x.unitName === 'Offers').length) {
        setContinueButtonTooltip(formatMessage('noBasketItemsFound'));
      } else if (selectedStep === _models.ShoppingStep.Checkout) {
        if (hasNoBasketItems) {
          setContinueButtonTooltip(formatMessage('noBasketItemsFound'));
        } else if (isMandatoryEmailMissing) {
          setContinueButtonTooltip(formatMessage('mandatoryEmailMissing'));
        } else if (isMandatoryPhoneMissing) {
          setContinueButtonTooltip(formatMessage('mandatoryPhoneMissing'));
        } else if (!isDeliveryChannelSelected) {
          setContinueButtonTooltip(formatMessage('deliveryChannelIsMissing'));
        } else if (!cardConsentIsFulfilled) {
          setContinueButtonTooltip(formatMessage('cardConsentIsRequired'));
        } else if (isMandatoryBillingAddressMissing) {
          setContinueButtonTooltip(formatMessage('mandatoryBillingAddressMissing'));
        } else if (billingState !== 'enabled') {
          setContinueButtonTooltip(formatMessage('continueClickIsRequired'));
        } else if (orderState === _models.OrderState.Pending) {
          setContinueButtonTooltip(formatMessage('loading'));
        } else if (orderState === _models.OrderState.Succeeded) {
          setContinueButtonTooltip(formatMessage('orderIsPlacedAlready'));
        } else {
          setContinueButtonTooltip(undefined);
        }
      } else {
        setContinueButtonTooltip(undefined);
      }
    }
  }, [addedItems, backorderItems.length, billingState, cardConsentIsFulfilled, formatMessage, isAnonymous, isDeliveryChannelSelected, isLoaded, isMandatoryBillingAddressMissing, isMandatoryEmailMissing, isMandatoryPhoneMissing, orderState, paymentModel, registrationFee, renewalFee, selectedStep, shoppingBagItems, shoppingBagOffers, subscriptions, unitDataCollection]);
  const handleStepChange = (0, _react.useCallback)(newStep => {
    if (isLoaded && !isAnonymous && newStep !== selectedStep) {
      if (shouldMutatePaymentModel && newStep === _models.ShoppingStep.Checkout) {
        void mutatePaymentModel();
        setShouldMutatePaymentModel(false);
      }
      setSelectedStep(newStep);
      window.scrollTo(0, 0);
      // analytics - update virtual page
      (0, _utils.dispatchVirtualPageEvent)(newStep, selectedStep);
    }
  }, [isAnonymous, isLoaded, mutatePaymentModel, selectedStep, setShouldMutatePaymentModel, shouldMutatePaymentModel]);

  /** Calls setSelectedStep internally while maintaining the virtual page for analytics consistently. */
  const setCurrentStep = (0, _react.useCallback)(newStep => handleStepChange(newStep), [handleStepChange]);
  const onRegistrationClose = (0, _react.useCallback)(() => {
    setIsRegistrationVisible(false);
  }, [setIsRegistrationVisible]);
  const getNavigationButtonsProps = (0, _react.useCallback)(() => {
    const offers = unitDataCollection.filter(x => x.unitName === 'Offers');
    let shoppingSteps = Object.values(_models.ShoppingStep).filter(x => typeof x === 'number');
    if (!offers.length) {
      shoppingSteps = shoppingSteps.filter(step => step !== _models.ShoppingStep.Offers);
    }
    return shoppingSteps.map((step, index) => {
      const {
        metaHierarchy,
        stepLabelKey: labelKey,
        navigationButtonTestId
      } = _utils.CheckoutStepsSettings[step];
      return {
        isDisabled: (Boolean(isAnonymous) || !isLoaded) && step !== _models.ShoppingStep.Basket,
        isSelected: selectedStep === step,
        navigationButtonTestId,
        onClick: () => {
          // analytics - dispatch user-action for clicked button
          (0, _utils.dispatchUserActionEvent)([_models.MetaHierarchy.CheckoutNavigation, metaHierarchy]);
          handleStepChange(step);
        },
        stepLabelKey: labelKey,
        stepNumber: index + 1
      };
    });
  }, [handleStepChange, isAnonymous, isLoaded, selectedStep, unitDataCollection]);
  const navigationButtonsProps = (0, _react.useMemo)(() => getNavigationButtonsProps(), [getNavigationButtonsProps]);
  const contextValue = (0, _react.useMemo)(() => ({
    activeProviderId,
    areCustomerCheckoutPrerequisitesLoading,
    billingState,
    continueButtonTooltip,
    customerCheckoutPrerequisites,
    isPaymentModelLoading,
    isRegistrationVisible,
    mutateCustomerCheckoutPrerequisites,
    navigationButtonsProps,
    onRegistrationClose,
    orderState,
    paymentModel,
    registrationId,
    selectedStep,
    setActiveProviderId,
    setBillingState,
    setCurrentStep,
    setIsRegistrationVisible,
    setOrderState,
    setRegistrationId,
    setSelectedStep
  }), [activeProviderId, areCustomerCheckoutPrerequisitesLoading, billingState, continueButtonTooltip, customerCheckoutPrerequisites, isPaymentModelLoading, isRegistrationVisible, mutateCustomerCheckoutPrerequisites, navigationButtonsProps, onRegistrationClose, orderState, paymentModel, registrationId, selectedStep, setActiveProviderId, setCurrentStep]);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_navigationContext.navigationContext.Provider, {
    value: contextValue,
    children: children
  });
};
exports.NavigationContextProvider = NavigationContextProvider;